import PlayerArea, { PlayerTabTitle } from './PlayerArea';
import { SetTitle, useLobby } from '../utils/Lobby';
import NewGameDialog from './NewGameDialog';
import GameOver from './GameOver';
import CardRow from './CardRow';
import MilitaryArea from './MilitaryArea';
import MilitaryBoard from './MilitaryBoard';
import CollapsedCard from './CollpasedCard';
import { GameStateContext } from '../context/GameStateContext';
import { IconText } from './Amount';
import CardDecks from './CardDecks';
import { CultureBoard } from './TableBoards';
import LeadersAndWonders from './LeadersAndWonders';
import TurnControls, { TurnButtons, PoliticalActions } from './TurnControls';
import SendFailureDialog from './SendFailureDialog';
import Journal from './Journal';
import Chat from './Chat';
import SinceLastTurn from './SinceLastTurn';
import { ReviewSteps, ReviewTurn } from './Review';
import React, {useEffect, useState, useRef, useCallback} from "react";
import { SeatColors, ColorText, ColorStrong } from './Constants/SeatColors';
import { RemoteAuthority, CreateGame, Get } from '../utils/RemoteAuthority';
import TopBar from './TopBar';
import { KeptTurn } from '../utils/KeptTurn';
import { IsMobile } from '../utils/Mobile';
import { useNewUI, useStaticButtons } from '../utils/Preferences';
const {Tab,Tabs} = require('react-bootstrap');
const GameEngine = require('@tta/engine/GameEngine');
const Review = require('@tta/engine/Review');
const { ChoicesFromSetup } = require('@tta/engine/Constants/CardVersions');
const { Phase } = require('@tta/engine/Moves/MoveTypes');

// "Viewing as": our own player (the only choice without the development tools), or, for testing, a player's number,
// or following whoever's turn it is
const Mine = 'mine';
const Follow = 'follow';

// The strip of the card row and the decks: how wide it is at its natural size (every card at its own width, side by
// side), and how far it may grow past that to fill a wide window (cards, text and all, evenly)
const StripWidth = 1460;
const MaxStripZoom = 1.5;
// The narrowest the page squeezes to: below it, the page runs off the right-hand edge (and scrolls) instead
const MinPageWidth = 1280;

// The game and the perspective are kept in the address (?game=1, or ?game=1&player=follow), so a reload or a shared
// link keeps them; and, looking back over a finished game, the point in it (?game=1&review=40: after 40 moves)
const readAddress = () => {
    const params = new URLSearchParams(window.location.search);
    return { gameId: params.get('game'), perspective: params.get('player') ?? Mine, review: params.get('review') };
};
const writeReviewAddress = (position) => {
    const params = new URLSearchParams(window.location.search);
    params.set('review', position);
    window.history.replaceState(null, '', `?${params}`);
};
// What the game page shows of a point in a game looked back over: the game then, with everything known (it is over),
// and the journal up to then
const ReviewView = (review, position) => ({ version: 0, viewer: null, frozen: false, state: review.StateAt(position),
    journal: review.JournalAt(position), sinceLastTurn: { events: [], changes: [] } });
const writeAddress = (gameId, perspective) => {
    const params = new URLSearchParams(window.location.search);
    params.set('game', gameId);
    if (perspective === Mine)
    {
        params.delete('player');
    } else {
        params.set('player', perspective);
    }
    // (a new game, once made, is addressed by its number)
    params.delete('new');
    window.history.replaceState(null, '', `?${params}`);
};


function Game() {
    var [gameViewModel, setGameViewModel] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const gameEngine = useRef(null);
    const authority = useRef(null);
    const [players, setPlayers] = useState([]);
    const [title, setTitle] = useState(null);
    const [perspective, setPerspective] = useState(readAddress().perspective);
    // Whether the server has the development tools (picking whose view to see); our seat in the game (-1: not in it)
    const [devTools, setDevTools] = useState(false);
    const [mySeat, setMySeat] = useState(-1);
    // Who sits where (logins), for a rematch; and whether the rematch dialog is open
    const [seats, setSeats] = useState([]);
    const [rematch, setRematch] = useState(false);
    // The action bar in place of the action panel's buttons for the same actions (the player's choice, on their profile)
    const [staticButtons] = useStaticButtons();
    // (the culture and military boards under the card row, in place of the military area on each board: see Preferences)
    const [newUI] = useNewUI();
    const { me, users } = useLobby();
    // The card decks' width, which the military area under them matches (they grow with the counts under them)
    const decksRef = useRef(null);
    const [decksWidth, setDecksWidth] = useState(376);
    // How much the strip of the card row and the decks is grown (1: its natural size; never shrunk: a narrower window
    // squeezes the spacing between cards, then cuts off the right-hand end)
    const stripRef = useRef(null);
    const [stripZoom, setStripZoom] = useState(1);
    // (measured once the game is shown, the decks then being on the page; the decks as they look, grown with the strip)
    const shown = gameViewModel !== null;
    useEffect(() => {
        const decks = decksRef.current;
        const strip = stripRef.current;
        if (!decks || !strip)
        {
            return undefined;
        }
        const measure = () => {
            setStripZoom(Math.round(Math.min(MaxStripZoom, Math.max(1, strip.clientWidth / StripWidth)) * 100) / 100);
            setDecksWidth(decks.getBoundingClientRect().width);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(decks);
        observer.observe(strip);
        return () => observer.disconnect();
    }, [shown, stripZoom]);

    // Fetches the view for the perspective (in follow mode, the current player's) and starts the turn controller on it
    const refresh = useCallback(async (wanted) => {
        try {
            const summary = await authority.current.FetchSummary();
            if (summary.ok === false)
            {
                setLoadError(summary.error);
                return;
            }
            setPlayers(summary.players);
            setMySeat(summary.mySeat);
            setSeats(summary.seats ?? []);
            setTitle(summary.title);
            SetTitle(`${summary.title} · Through the Ages`);
            // Following: whoever the game waits on (the player deciding, or else the current player); our own player
            // (a game we are not in, with the development tools: following)
            const acting = summary.actingPlayer ?? summary.currentPlayer;
            const viewer = wanted === Follow || (wanted === Mine && summary.mySeat < 0) ? acting : wanted === Mine ? summary.mySeat : Number(wanted);
            const view = await authority.current.FetchView(viewer);
            if (view.ok === false)
            {
                setLoadError(view.error);
                return;
            }
            // (this turn's moves so far, if the page was reloaded during it)
            gameEngine.current = new GameEngine(null, setGameViewModel, { authority: authority.current, view, viewer,
                keep: KeptTurn(readAddress().gameId, viewer) });
        } catch (e) {
            setLoadError(`Could not reach the server: ${e.message}`);
        }
    }, []);

    // Looking back over a finished game: { data (the engine's Review), position }, or null when playing it. The game is
    // shown read-only, at the position, from a turn controller of its own with nothing to commit to
    const [review, setReview] = useState(null);
    const showReview = (data, position) => {
        const at = Math.max(0, Math.min(data.Length, position));
        const view = ReviewView(data, at);
        if (gameEngine.current?.reviewing)
        {
            gameEngine.current.loadView(view);
        } else {
            gameEngine.current = new GameEngine(null, setGameViewModel, { authority: { committedMoves: [] }, view, viewer: null });
            gameEngine.current.reviewing = true;
        }
        setReview({ data, position: at });
        writeReviewAddress(at);
    };
    const startReview = async (position) => {
        const summary = await authority.current.FetchSummary();
        if (summary.ok === false)
        {
            setLoadError(summary.error);
            return;
        }
        setPlayers(summary.players);
        setMySeat(summary.mySeat);
        setTitle(summary.title);
        SetTitle(`${summary.title} (review) · Through the Ages`);
        const history = await authority.current.FetchHistory();
        if (history.ok === false)
        {
            setLoadError(history.error);
            return;
        }
        showReview(new Review(history.initial, history.commits), Number(position) || 0);
    };
    const gameAddress = (extra = '') => `./?game=${readAddress().gameId}${extra}`;

    // Join the game in the address, or start one (once: React runs this twice in development)
    const started = useRef(false);
    useEffect(() => {
        if (started.current)
        {
            return;
        }
        started.current = true;
        let stop = () => {};
        (async () => {
            try {
                let { gameId, perspective } = readAddress();
                // (without the development tools, always our own player)
                const me = await Get('/games/me');
                // (not signed in: the front page signs us in, then comes back here)
                if (me.ok === false && me.signInForm)
                {
                    window.location.href = `./?next=${encodeURIComponent(window.location.search)}`;
                    return;
                }
                setDevTools(!!me.devTools);
                if (!me.devTools && perspective !== Mine)
                {
                    perspective = Mine;
                    setPerspective(Mine);
                }
                if (!gameId)
                {
                    gameId = await CreateGame();
                }
                writeAddress(gameId, perspective);
                authority.current = new RemoteAuthority(gameId);
                // (looking back over it: nothing changes any more, so nothing to listen for)
                if (readAddress().review !== null)
                {
                    await startReview(readAddress().review);
                    return;
                }
                await refresh(perspective);
                // When the game changes (someone else's moves, or our own committed turn), catch up; a chat message joins
                // the chat (none: some may have been missed, fetch it again)
                stop = authority.current.Subscribe(async (version) => {
                    const engine = gameEngine.current;
                    const following = readAddress().perspective === Follow;
                    const summary = following ? await authority.current.FetchSummary() : null;
                    if (!engine || (following && (summary.actingPlayer ?? summary.currentPlayer) !== engine.viewer))
                    {
                        refresh(readAddress().perspective);
                    } else if (engine.waiting) {
                        // (our own moves on their way: the server's answer brings the game as it is now)
                    } else if (engine.sendFailure?.retry && version > engine.view.version) {
                        // Sending our moves seemed to fail, but the game changed: perhaps they did arrive (the answer was
                        // lost). Sent again: the server says so if they did, and otherwise takes them now
                        engine.retry();
                    } else if (version > engine.view.version) {
                        // Someone else's move or decision: keep this turn's moves on top of it
                        engine.rebase(await authority.current.FetchView(engine.viewer));
                    }
                }, (message) => message ? AddChatMessage(message) : setChatReload(count => count + 1));
            } catch (e) {
                setLoadError(`Could not reach the server: ${e.message}`);
            }
        })();
        return () => { stop(); started.current = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changePerspective = (value) => {
        setPerspective(value);
        writeAddress(readAddress().gameId, value);
        refresh(value);
    };

    // Open the board of the player looking (or, looking at everything, whoever's turn it is)
    const [activeTab, setActiveTab] = useState(null);
    // (Moving a worker, picked on the board: that player's board)
    const focusIndex = gameViewModel ? (gameViewModel.boardSelection?.player ?? gameViewModel.viewer ?? gameViewModel.currentPlayer) : null;
    const focusName = focusIndex !== null ? gameViewModel?.playerViewModels[focusIndex]?.name : undefined;
    useEffect(() => {
        if (focusName !== undefined)
        {
            setActiveTab(focusName);
        }
    }, [focusName]);

    // The game's chat as the player looking sees it: { messages, seen (the last one they have read) }; the unread ones
    // (others' messages after it) counted on its tab, and read once the tab is open
    const [chat, setChat] = useState({ messages: [], seen: 0 });
    const [chatReload, setChatReload] = useState(0);
    const chatPlayer = gameViewModel?.viewer ?? null;
    const AddChatMessage = (message) => setChat(current => current.messages.some(m => m.id === message.id) ? current
        : { ...current, messages: [...current.messages, message] });
    useEffect(() => {
        if (chatPlayer === null || !authority.current)
        {
            return;
        }
        authority.current.FetchChat(chatPlayer).then(result => result.messages && setChat({ messages: result.messages, seen: result.seen }))
            .catch(() => {});
    }, [chatPlayer, chatReload]);
    const lastChat = chat.messages.at(-1)?.id ?? 0;
    // (on a phone, the chat is a section of its own: see MobileLayout)
    const [section, setSection] = useState('turn');
    const chatOpen = IsMobile ? section === 'chat' : activeTab === 'chat';
    useEffect(() => {
        if (chatOpen && chatPlayer !== null && lastChat > chat.seen)
        {
            setChat(current => ({ ...current, seen: lastChat }));
            authority.current.SeeChat(chatPlayer, lastChat).catch(() => {});
        }
    }, [chatOpen, chatPlayer, lastChat, chat.seen]);
    // (on a phone, picking a card on the board, e.g. to destroy it: the boards, to pick it on)
    const boardPicking = !!gameViewModel?.boardSelection;
    useEffect(() => {
        if (IsMobile && boardPicking)
        {
            setSection('boards');
        }
    }, [boardPicking]);
    const unreadChat = chat.messages.filter(message => message.id > chat.seen && message.seat !== chatPlayer).length;
    const sendChat = async (text) => {
        const result = await authority.current.SendChat(chatPlayer, text);
        if (result.ok)
        {
            AddChatMessage(result.message);
        }
        return result;
    };

    var content = (<div className="tta-game-loading">{loadError ?? 'Loading the game…'}</div>)

    if (gameViewModel != null)
    {
        const currentPlayer = gameViewModel.playerViewModels[gameViewModel.currentPlayer];
        // Each seat's color as this viewer sees it (their own, if they picked one: see SeatColors.js)
        const seatColors = SeatColors(gameViewModel.playerViewModels.length, mySeat, me?.color);
        const playerColors = seatColors.map(ColorText);
        // (and for their cubes and markers: the yellow of their board, brighter than the yellow names are written in)
        const cubeColors = seatColors.map(ColorStrong);
        // [Current player] - [the state the game is in], then who it waits on, if not just them (the cards involved are for
        // the players in it to see, below); in the title bar, after the age and round (once the game is over, they say so)
        const phase = !review && gameViewModel.status.state !== 'over' && <span className="tta-title-phase">
                <span style={{ color: playerColors[gameViewModel.currentPlayer] }}>{currentPlayer.name}</span> - {gameViewModel.status.label}
                {gameViewModel.status.waitingOn.some(name => name !== currentPlayer.name) && <> - waiting for {gameViewModel.status.waitingOn.map((name, index) =>
                    <span key={name}>{index > 0 && ', '}<span style={{ color: playerColors[gameViewModel.playerViewModels.findIndex(p => p.name === name)] }}>{name}</span></span>)}</>}
            </span>;
        // The page's parts, laid out for a desktop below, or for a phone (MobileLayout)
        const titleBar = <div className="tta-title">
                {title ?? 'Through the Ages'} - <span className="tta-title-info">{gameViewModel.age} round {gameViewModel.round}
                    {gameViewModel.finished ? ' - game over' : gameViewModel.finalRound === gameViewModel.round ? ' - last round'
                        : gameViewModel.finalRound !== null ? ' - last round next' : ''}</span>
                {phase}
                {/* A public mix's leaders and wonders, opened from here */}
                <LeadersAndWonders Pool={gameViewModel.leadersAndWonders}/>
                {/* Once the game is over, a rematch: the same players and settings (admins run the site and play no games) */}
                {me && !me.admin && gameViewModel.finished &&
                    <button type="button" className="tta-title-button" onClick={() => setRematch(true)}>Rematch</button>}
                {/* Once it is over, looking back over it turn by turn */}
                {gameViewModel.finished && !review &&
                    <button type="button" className="tta-title-button" onClick={() => { window.location.href = gameAddress('&review=0'); }}>Review</button>}
            </div>;
        // (reviewing a finished game: the steps through it, on a line of their own)
        const phaseLine = review && <div className="tta-phase">
                <ReviewSteps Review={review.data} Position={review.position} OnGo={(position) => showReview(review.data, position)} PlayerColors={playerColors}/>
            </div>;
        const cardRow = <CardRow
                    Cards={gameViewModel === null || gameViewModel.cardRow === null ? [] : gameViewModel.cardRow} PlayerCount = {gameViewModel?.playerViewModels?.length}
                    TakeMoves={review ? {} : gameViewModel.decisionRow?.moves ?? (gameViewModel.isViewersTurn ? gameViewModel.cardRowMoves : {})}
                    OnTake={(move) => !review && gameEngine.current.submitMove(move)}
                    TakenFromRow={gameViewModel.takenFromRow}
                    Blocked={gameViewModel.decisionRow?.blocked ?? gameViewModel.cardRowBlocked}
                    CostNotes={review ? undefined : gameViewModel.cardRowCostNotes}
                    RetractableCardIds={review ? [] : gameViewModel.retractableCardIds}
                    OnPutBack={(cardId) => gameEngine.current.retractCardTake(cardId)}
                    />;
        const cardDecks = <CardDecks
                        MilitaryCandidates={gameViewModel.militaryDeckCandidates}
                        Civil={gameViewModel.civilDeck}
                        Military={gameViewModel.militaryDeck}
                        Current={gameViewModel.currentEvents}
                        CurrentKnown={gameViewModel.currentEventsKnown}
                        Future={gameViewModel.futureEvents}
                        InOrder={!!review}
                    />;
        // (looking back over the game: what was done on the turn, in place of the actions)
        const recap = gameViewModel.finished
                    ? <GameOver Finished={gameViewModel.finished} PlayerColors={playerColors} Players={gameViewModel.playerViewModels} Prepared={gameViewModel.prepared}/>
                    : review ? null : <SinceLastTurn GameViewModel={gameViewModel} PlayerColors={playerColors}/>;
        const turnControls = review
            ? <ReviewTurn Review={review.data} Position={review.position} OnGo={(position) => showReview(review.data, position)}
                PlayerColors={playerColors} OnLeave={() => { window.location.href = gameAddress(); }}/>
            : <TurnControls GameViewModel={gameViewModel} GameEngine={gameEngine.current} PlayerColors={playerColors} StaticButtons={staticButtons}
                NewUI={newUI && !IsMobile}/>;
        // (with static buttons, its tactics are copied there with the action bar's Tactic button)
        const militaryArea = <MilitaryArea Military={gameViewModel.military} PlayerColors={playerColors} CubeColors={cubeColors}
            Bar={staticButtons && !review ? gameViewModel.actionBar : null} GameEngine={gameEngine.current}/>;
        const militaryBoard = <MilitaryBoard Military={gameViewModel.military} CubeColors={cubeColors}
            Bar={staticButtons && !review ? gameViewModel.actionBar : null} GameEngine={gameEngine.current} Olympics={gameViewModel.olympics} Current={gameViewModel.currentPlayer}/>;
        // (with the new UI on a desktop, the boards under the card row show the military area, the Nobel Prize and the
        // Olympic Games instead)
        const boards = newUI && !IsMobile;
        const sideBoxes = <>
                    {/* Alfred Nobel by the culture track: the prize everyone can score */}
                    {!boards && gameViewModel.nobelPrize &&
                        <div className="tta tta-left tta-nobel-box" title="4 culture at the end of a turn in which a civilization developed at least 2 technologies">
                            <div className="tta-since-title">Nobel Prize</div>
                            <CollapsedCard card={gameViewModel.nobelPrize.card}/>
                            <div className="tta-muted"><IconText text="+4 culture at the end of a turn with 2 or more technologies developed"/></div>
                        </div>}
                    {/* Pierre de Coubertin's Olympic Games, until his player's next turn */}
                    {!boards && gameViewModel.olympics &&
                        <div className="tta tta-left tta-nobel-box tta-olympics-box" title="Whoever plays a war or aggression while they last loses 8 culture">
                            <div className="tta-since-title">Olympic Games</div>
                            <CollapsedCard card={gameViewModel.olympics.card}/>
                            <div className="tta-muted"><IconText text={`Until ${gameViewModel.olympics.name}'s next turn: a war or aggression costs 8 culture`}/></div>
                        </div>}
                    {/* (on a desktop, the military area is at the top right of every board) */}
                    {IsMobile && militaryArea}
                </>;
        const playerTabs = (gameViewModel === null) ?
                    <p>game view null</p>
                : gameViewModel.playerViewModels.map((playerViewModel,idx) => {
                    return (<Tab
                    tabClassName={"color-tab-" + seatColors[idx]}
                    key={playerViewModel.name}
                    eventKey = {playerViewModel.name}
                    title = {<PlayerTabTitle PlayerViewModel={playerViewModel} NewUI={boards}/>}
                    // (whose board it is: a border in their color, around a board of the page's own colors)
                    className="tta-player-board" style={{ borderColor: ColorStrong(seatColors[idx]) }}>
                            <PlayerArea
                                PlayerViewModel={playerViewModel}
                                GameEngine={gameEngine.current}
                                IsCurrentPlayer={idx === gameViewModel.currentPlayer && gameViewModel.isViewersTurn && !review}
                                RetractableCardIds={gameViewModel.retractableCardIds}
                                UseRestricted={gameViewModel.useRestricted}
                                Pacts={gameViewModel.pacts.filter(pact => pact.a === idx || pact.b === idx)}
                                Wars={gameViewModel.wars.filter(war => war.aggressor === idx || war.defender === idx)}
                                LastTurn={gameViewModel.lastTurns[idx]}
                                BoardSelection={gameViewModel.boardSelection?.player === idx ? gameViewModel.boardSelection : null}
                                // (the action bar on the board of the player looking: the current player's, when looking at everything)
                                ActionBar={staticButtons && !review && idx === (gameViewModel.viewer ?? gameViewModel.currentPlayer) ? gameViewModel.actionBar : null}
                                TurnButtons={<TurnButtons GameViewModel={gameViewModel} GameEngine={gameEngine.current}/>}
                                Military={IsMobile || boards ? null : militaryArea}
                                NewUI={boards}
                                Political={gameViewModel.phase === Phase.Political
                                    ? <PoliticalActions GameViewModel={gameViewModel} GameEngine={gameEngine.current}/> : null}
                                Resigned={gameViewModel.resigned[idx]}
                                PlayerColors={playerColors}
                                CubeColors={cubeColors}
                                // (with the new UI, the Trade Routes Agreement's choice is on its card, on the board of the
                                // player whose turn it is, while it is the viewer's turn and not the political phase)
                                TradeRoute={boards && !review && gameViewModel.isViewersTurn && gameViewModel.phase !== Phase.Political
                                    && idx === gameViewModel.currentPlayer && gameViewModel.tradeRoute
                                    ? { ...gameViewModel.tradeRoute, use: gameViewModel.useTradeRoute } : null}
                                PlayerNames={gameViewModel.playerViewModels.map(p => p.name)}
                            ></PlayerArea>
                    </Tab>)
                });
        const journal = <Journal GameViewModel={gameViewModel} PlayerColors={playerColors}/>;
        const chatBox = <Chat Messages={chat.messages} Players={gameViewModel.playerViewModels.map(p => p.name)} PlayerColors={playerColors}
                        OnSend={sendChat}/>;
        const unreadBadge = unreadChat > 0 && <span className="tta-chat-unread" title={`${unreadChat} unread`}>{unreadChat}</span>;

        content = IsMobile ? <MobileLayout Section={section} OnSection={setSection} Head={<>{titleBar}{phaseLine}</>}
            Sections={{
                turn: <>
                    <div className="tta-m-scroll">{cardRow}</div>
                    <div className="tta-m-pad">{recap}</div>
                    <div className="tta-m-pad">{turnControls}</div>
                </>,
                table: <>
                    <div className="tta-m-scroll">{cardDecks}</div>
                    <div className="tta-m-pad">{sideBoxes}</div>
                </>,
                boards: <>
                    {/* (an action picked on the board, e.g. Destroy, is done from the turn's actions) */}
                    {gameViewModel.boardSelection && <div className="tta-m-board-pick">
                        Pick a card on the board, then <button className="tta-button" onClick={() => setSection('turn')}>back to the actions</button>
                    </div>}
                    <Tabs activeKey={activeTab ?? undefined} onSelect={(key) => setActiveTab(key)}>{playerTabs}</Tabs>
                </>,
                journal: <div className="tta-m-pad">{journal}</div>,
                chat: chatBox,
            }}
            Badges={{ chat: unreadBadge }}/>
        : <div className="tta">
            {titleBar}
            {phaseLine}
            {/* Shared by everyone, so it stays visible whichever player's area is open.
                One strip across the page: a wide window grows it, cards and all (up to MaxStripZoom); spare width then
                goes into the spacing between cards, and once that is used up a narrower window cuts off the right-hand
                end rather than shrinking anything or scrolling. */}
            <div ref={stripRef} style={{ overflow: 'hidden' }}>
            <div style={{ zoom: stripZoom, display: 'flex', flexWrap: 'nowrap', overflow: 'hidden', padding: '6px 4px 0', gap: '4px' }}>
                <div style={{ flex: '1 0 auto' }}>
                    {cardRow}
                </div>
                {/* (as wide as the decks: what goes under them wraps rather than widening them) */}
                <div style={{ flex: '0 0 auto', width: 'min-content' }} ref={decksRef}>
    {cardDecks}
                </div>
            </div>
            </div>
            {/* The boards in the middle of the table, for everyone: culture and military, side by side across the page */}
            {boards &&
                <div className="tta-table-boards">
                    <CultureBoard Players={gameViewModel.playerViewModels} Colors={cubeColors} Resigned={gameViewModel.resigned} Nobel={gameViewModel.nobelPrize}
                        Current={gameViewModel.currentPlayer}/>
                    {militaryBoard}
                </div>}
            {/* The recap across the whole width; under it the actions, with the Nobel Prize and Olympic Games (while they
                are on, without the new UI) beside them, as wide as the decks. The military area is on every board, at its top right
                (with the new UI, on the military board under the card row). */}
            <div style={{ padding: '0 8px' }}>
                {recap}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '0 8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {turnControls}
                </div>
                <div style={{ flex: `0 0 ${decksWidth}px` }}>
                    {sideBoxes}
                </div>
            </div>
            <Tabs activeKey={activeTab ?? undefined} onSelect={(key) => setActiveTab(key)}>
                {playerTabs}
                <Tab tabClassName="color-tab-Journal" eventKey="journal" title="Journal">
                    {journal}
                </Tab>
                {!review && <Tab tabClassName="color-tab-Chat" eventKey="chat"
                    title={<>Chat{unreadBadge}</>}>
                    {chatBox}
                </Tab>}
            </Tabs>
        </div>
    }
    // For testing, in the top bar: whose view of the game is shown (each player sees only what they may know)
    const viewingAs = devTools && !review && players.length > 0 && <label className="tta-perspective">
        Viewing as{' '}
        <select value={perspective} onChange={(e) => changePerspective(e.target.value)}>
            {mySeat >= 0 && <option value={Mine}>me ({players[mySeat]})</option>}
            <option value={Follow}>whoever is acting</option>
            {players.map((name, index) => <option key={index} value={index}>{name}</option>)}
        </select>
    </label>;

    // (the game as this player sees it, for hovers that work something out from it, e.g. what a card would score now)
    return <GameStateContext.Provider value={gameEngine.current && gameViewModel
        ? { gameState: gameEngine.current.gameState, viewer: gameViewModel.viewer ?? gameViewModel.currentPlayer, colors: SeatColors(gameViewModel.playerViewModels.length, mySeat, me?.color).map(ColorText) }
        : null}><div style={IsMobile ? undefined : { minWidth: MinPageWidth }}><TopBar extra={viewingAs}/>{content}</div>
        {/* Sending moves failed (ending the turn says so in its own dialog) */}
        {gameViewModel?.sendFailure && !gameViewModel.sendFailure.endTurn &&
            <SendFailureDialog Failure={gameViewModel.sendFailure} Sending={gameViewModel.waiting}
                OnRetry={() => gameEngine.current.retry()} OnClose={() => gameEngine.current.dismissSendFailure()}/>}
        {/* (a test game's seats are no one's: its players by name, those who are also people here) */}
        {rematch && me && <NewGameDialog me={me} users={users} onClose={() => setRematch(false)}
            initial={{ seats: seats.map((login, seat) => login ?? users.find(user => user.name === players[seat])?.login),
                choices: ChoicesFromSetup(gameViewModel?.setup) }}/>}
    </GameStateContext.Provider>;
}

// The game on a phone: what the game is doing along the top (Head), then one section at a time, picked from a bar
// along the bottom. The sections follow the desktop page: the turn (the card row, the recap and the actions, as there,
// top to bottom), the table (the decks and the military area, beside the actions there), the players' boards, the
// journal and the chat (its tabs there). All stay on the page, hidden, so nothing in one (a dialog, a message being
// written) is lost by looking at another.
const MobileSections = [['turn', 'Turn'], ['table', 'Table'], ['boards', 'Boards'], ['journal', 'Journal'], ['chat', 'Chat']];

function MobileLayout({Head, Sections, Section, OnSection, Badges})
{
    return <div className="tta tta-m-game">
        <div className="tta-m-head">{Head}</div>
        {MobileSections.map(([key]) =>
            <div key={key} className={`tta-m-section tta-m-section-${key}`} style={{ display: key === Section ? undefined : 'none' }}>{Sections[key]}</div>)}
        <nav className="tta-m-nav">
            {MobileSections.map(([key, label]) =>
                <button key={key} className={key === Section ? 'on' : undefined} onClick={() => { OnSection(key); window.scrollTo(0, 0); }}>
                    {label}{Badges[key]}
                </button>)}
        </nav>
    </div>;
}

export default Game;